import { CommonModule } from '@angular/common'
import { Component, computed, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { EditorComponent } from '@app/modules/shared/components/editor/editor.component'
import { ToastrService } from '@app/services/toastr.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { PersonaFieldSelection, SupportRequest, SupportRequestFormData, SupportRequestType } from '../../interfaces/support-request.interface'
import { SupportRequestService, SupportRequestTypeLabels } from '../../services/support-request.service'

/**
 * Support Request Modal Component
 * Modal for creating and viewing support requests for an event
 */
@Component({
  selector: 'ngx-support-request-modal',
  templateUrl: './support-request-modal.component.html',
  styleUrls: ['./support-request-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ModalComponent, EditorComponent]
})
export class SupportRequestModalComponent implements OnChanges {
  @Input() event: ServerRepresentedEvent | null = null
  @Output() modalClosed = new EventEmitter<void>()

  private readonly _destroyRef = inject(DestroyRef)
  public readonly modalId = 'support-request-modal'

  /** Maximum character limit for description */
  public readonly MAX_DESCRIPTION_LENGTH = 1800

  /** Form group */
  public requestForm: FormGroup

  /** Support request types for dropdown */
  public readonly requestTypes = Object.values(SupportRequestType)
  public readonly requestTypeLabels = SupportRequestTypeLabels

  /** Event personas */
  public personas = signal<Persona[]>([])

  /** Selected personas */
  public selectedPersonas = signal<string[]>([])

  /** Field selections */
  public fieldSelections = signal<PersonaFieldSelection[]>([])

  /** Description from editor */
  public description = signal<string>('')

  /**
   * Character count reported directly by the shared editor component.
   * This uses the same counting rules as the editor's own maxCharacterLimit logic,
   * ensuring the label and "content size limit" behavior stay perfectly in sync.
   */
  public descriptionCharCount = signal<number>(0)

  /** Check if description exceeds character limit */
  public isDescriptionOverLimit = computed(() => {
    return this.descriptionCharCount() > this.MAX_DESCRIPTION_LENGTH
  })

  /** Loading states */
  public loading = signal(false)
  public submitting = signal(false)
  public loadingPersonas = signal(false)

  /** Historical support requests */
  public historicalRequests = signal<SupportRequest[]>([])

  /** Show field selector */
  public showFieldSelector = signal(false)

  /** Editor key to force recreation when event changes */
  public editorKey = signal<string>('')

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _modalService: ModalService,
    private readonly _eventService: EventService,
    private readonly _supportRequestService: SupportRequestService,
    private readonly _toastr: ToastrService,
    private readonly _alertService: AlertService
  ) {
    this.requestForm = this._fb.group({
      type: ['', Validators.required]
    })
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Load event data whenever the event input changes and has a valid value
    if (changes['event']) {
      this._resetForm()
      // Update editor key to force recreation when event changes
      if (changes['event'].currentValue?.id) {
        const newKey = crypto.randomUUID()
        this.editorKey.set(newKey)
        this._loadEventData()
      } else this.editorKey.set('')
    }
  }

  /**
   * Load event data (personas and historical requests)
   */
  private _loadEventData(): void {
    if (!this.event?.id) return

    this.loading.set(true)
    this.loadingPersonas.set(true)

    // Load personas
    this._eventService
      .getEventForms(this.event.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (personas) => {
          this.personas.set(personas || [])
          this.loadingPersonas.set(false)
        },
        error: () => {
          this.loadingPersonas.set(false)
        }
      })

    // Extract historical support requests directly from event options (no extra API call)
    const options = (this.event as any).options || {}
    const requests = options.supportRequests || []
    this.historicalRequests.set(requests)
    this.loading.set(false)
  }

  /**
   * Toggle persona selection
   */
  public togglePersonaSelection(personaSlug: string): void {
    const current = this.selectedPersonas()
    const index = current.indexOf(personaSlug)

    if (index > -1) {
      // Remove persona
      this.selectedPersonas.set(current.filter((s) => s !== personaSlug))
      // Remove from field selections
      this.fieldSelections.set(this.fieldSelections().filter((f) => f.personaSlug !== personaSlug))
    } else {
      // Add persona
      this.selectedPersonas.set([...current, personaSlug])
    }
  }

  /**
   * Check if persona is selected
   */
  public isPersonaSelected(personaSlug: string): boolean {
    return this.selectedPersonas().includes(personaSlug)
  }

  /**
   * Toggle field selection for a persona
   */
  public toggleFieldSelection(personaSlug: string, personaName: string, field: PersonaField): void {
    const current = this.fieldSelections()
    const personaSelection = current.find((f) => f.personaSlug === personaSlug)

    if (personaSelection) {
      const fieldIndex = personaSelection.fieldNames.indexOf(field.name)
      if (fieldIndex > -1) {
        // Remove field
        personaSelection.fieldNames.splice(fieldIndex, 1)
        if (personaSelection.fields) {
          personaSelection.fields = personaSelection.fields.filter((f) => f.name !== field.name)
        }
        if (personaSelection.fieldNames.length === 0) {
          // Remove persona selection if no fields
          this.fieldSelections.set(current.filter((f) => f.personaSlug !== personaSlug))
        } else {
          this.fieldSelections.set([...current])
        }
      } else {
        // Add field
        personaSelection.fieldNames.push(field.name)
        if (!personaSelection.fields) {
          personaSelection.fields = []
        }
        personaSelection.fields.push(field)
        this.fieldSelections.set([...current])
      }
    } else {
      // Create new persona selection
      this.fieldSelections.set([
        ...current,
        {
          personaSlug,
          personaName,
          fieldNames: [field.name],
          fields: [field]
        }
      ])
    }
  }

  /**
   * Check if field is selected
   */
  public isFieldSelected(personaSlug: string, fieldName: string): boolean {
    const personaSelection = this.fieldSelections().find((f) => f.personaSlug === personaSlug)
    return personaSelection ? personaSelection.fieldNames.includes(fieldName) : false
  }

  /**
   * Get persona by slug
   */
  public getPersonaBySlug(slug: string): Persona | undefined {
    return this.personas().find((p) => p.slug === slug)
  }

  /**
   * Handle description change from editor
   */
  public onDescriptionChange(html: string): void {
    this.description.set(html)
  }

  /**
   * Receive live character count updates from the editor so our UI counter
   * and validation use the exact same character measurement as the editor.
   * @param count number
   */
  public onDescriptionCharacterCountChange(count: number): void {
    this.descriptionCharCount.set(count)
  }

  /**
   * Submit support request
   */
  public submitRequest(): void {
    if (this.requestForm.invalid || !this.description() || !this.event?.id) {
      this._toastr.error('Please fill in all required fields', 'Validation Error')
      return
    }

    // Check character limit
    if (this.isDescriptionOverLimit()) {
      this._alertService.alert(Alert.ERROR, `Description must be ${this.MAX_DESCRIPTION_LENGTH} characters or less. Current: ${this.descriptionCharCount()} characters.`)
      return
    }

    this.submitting.set(true)

    // Map persona slugs to names for display
    const personaNames = this.selectedPersonas()
      .map((slug) => this.getPersonaBySlug(slug)?.name)
      .filter((name) => name !== undefined) as string[]

    const formData: SupportRequestFormData = {
      type: this.requestForm.value.type,
      description: this.description(),
      personas: this.selectedPersonas(),
      personaNames: personaNames,
      fields: this.fieldSelections()
    }

    this._supportRequestService
      .createSupportRequest(this.event, formData)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (request) => {
          this.submitting.set(false)

          if (request.slackMessageSent) {
            this._toastr.success('Support request submitted and Slack notification sent!', 'Success')
          } else {
            this._toastr.success('Support request submitted (Slack notification failed)', 'Partial Success')
          }

          // Add to historical requests
          this.historicalRequests.set([request, ...this.historicalRequests()])

          // Reset form
          this._resetForm()

          // Close the modal after successful submission
          this.closeModal()
        },
        error: (error) => {
          this.submitting.set(false)
          this._toastr.error('Failed to submit support request', 'Error')
          console.error('Support request error:', error)
        }
      })
  }

  /**
   * Toggle fulfilled status of a historical request
   */
  public toggleFulfilled(request: SupportRequest): void {
    if (!this.event?.id) return

    const newStatus = !request.fulfilled

    this._supportRequestService
      .updateFulfilledStatus(this.event, request.id, newStatus)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Update local state
          const updated = this.historicalRequests().map((r) => (r.id === request.id ? { ...r, fulfilled: newStatus } : r))
          this.historicalRequests.set(updated)
          this._toastr.success(`Request marked as ${newStatus ? 'fulfilled' : 'pending'}`, 'Updated')
        },
        error: () => {
          this._toastr.error('Failed to update fulfilled status', 'Error')
        }
      })
  }

  /**
   * Reset form
   */
  private _resetForm(): void {
    this.requestForm.reset()
    this.selectedPersonas.set([])
    this.fieldSelections.set([])
    this.description.set('')
    this.showFieldSelector.set(false)
  }

  /**
   * Handle modal close event from modal component
   * Called when user clicks X button or presses Escape
   * Modal is already toggled by the modal component, so don't toggle again
   */
  public onModalCloseEvent(): void {
    this._cleanupAndNotify()
  }

  /**
   * Programmatically close the modal
   * Called when clicking Cancel button or after successful submission
   * We need to toggle the modal ourselves
   */
  public closeModal(): void {
    this._modalService.toggleModal(this.modalId)
    this._cleanupAndNotify()
  }

  /**
   * Common cleanup and notification logic
   */
  private _cleanupAndNotify(): void {
    // Emit the event to notify parent
    this.modalClosed.emit()

    // Clean up form and data
    this._resetForm()
    this.personas.set([])
    this.historicalRequests.set([])
    this.loading.set(false)
    this.loadingPersonas.set(false)
  }

  /**
   * Get description preview (strip HTML and truncate)
   */
  public getDescriptionPreview(html: string, maxLength: number = 100): string {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    const text = tmp.textContent || tmp.innerText || ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  /**
   * Toggle field selector visibility
   */
  public toggleFieldSelector(): void {
    this.showFieldSelector.set(!this.showFieldSelector())
  }

  /**
   * Get formatted date
   */
  public formatDate(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}
